import createMediaApi from '@rhg/libs/api/create-media-api';
import useAuth from '@rhg/libs/auth0/use-auth';
import { invalidateQueryKey, queryKeys, useQuery } from '@rhg/libs/react-query';
import { useCallback, useMemo } from 'react';
import { useCircle } from '../use-circle';

export const recentAndFavoriteGuidesQueryOptions = {
    queryKey: function ({ circleId }: { circleId: number }) {
        return [queryKeys.recentAndFavoriteGuides, circleId];
    },
    queryFn: async function ({
        getAccessTokenSilently,
        circleId,
        circleAuthorizationToken,
    }: {
        getAccessTokenSilently: () => Promise<string>;
        circleId: number;
        circleAuthorizationToken: string;
    }) {
        const mediaApi = createMediaApi(getAccessTokenSilently);

        const response = await mediaApi.getClinicalProgramGuide({
            circleAuthorizationToken,
            recipientId: circleId,
        });

        return response.data;
    },
    queryOptions: function ({
        isAuthenticated,
        circleId,
        circleAuthorizationToken,
        includeFetch,
    }: {
        isAuthenticated: boolean;
        circleId: number;
        circleAuthorizationToken: string;
        includeFetch: boolean;
    }) {
        return {
            staleTime: 1000 * 60 * 15, // 15 minutes since we invalidate on favorite and play start
            enabled: !!(
                includeFetch &&
                isAuthenticated &&
                circleId > 0 &&
                circleAuthorizationToken
            ),
        };
    },
};

export function useRecentAndFavoriteGuides(
    circleId: number,
    includeFetch = true
) {
    const { isAuthenticated, getAccessTokenSilently } = useAuth();
    const { circle } = useCircle(circleId);
    const circleAuthorizationToken = circle?.authorizationJWT ?? '';
    const { queryKey, queryFn, queryOptions } =
        recentAndFavoriteGuidesQueryOptions;

    const key = useMemo(() => queryKey({ circleId }), [circleId, queryKey]);
    const queryOptionValues = queryOptions({
        isAuthenticated,
        circleId,
        circleAuthorizationToken,
        includeFetch,
    });

    const { data, ...otherProps } = useQuery({
        queryKey: key,
        queryFn: async () =>
            queryFn({
                getAccessTokenSilently,
                circleId,
                circleAuthorizationToken,
            }),
        ...queryOptionValues,
    });

    const refresh = useCallback(() => {
        invalidateQueryKey(key);
    }, [key]);

    return {
        refresh,
        ...data,
        ...otherProps,
    };
}
